import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/InstagramInteractionMentionedPostDetails.graphql';
import { Query, QueryinstagramInteractionMentionedPostDetailsArgs } from '../../__generated__/globalTypes';

export const useInstagramInteractionMentionedPostDetailsQuery = (
  options?: QueryHookOptions<Query, QueryinstagramInteractionMentionedPostDetailsArgs>
) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QueryinstagramInteractionMentionedPostDetailsArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useInstagramInteractionMentionedPostDetailsLazyQuery = (
  options?: LazyQueryHookOptions<Query, QueryinstagramInteractionMentionedPostDetailsArgs>
): QueryResult<Query, QueryinstagramInteractionMentionedPostDetailsArgs> & {
  getInstagramInteractionMentionedPostDetails: LazyQueryExecFunction<
    Query,
    QueryinstagramInteractionMentionedPostDetailsArgs
  >;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getInstagramInteractionMentionedPostDetails, result] = useLazyQuery<
    Query,
    QueryinstagramInteractionMentionedPostDetailsArgs
  >(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getInstagramInteractionMentionedPostDetails, ...result };
};

export const useInstagramInteractionMentionedPostDetailsPromiseQuery = () => {
  const client = useApolloClient();

  const getInstagramInteractionMentionedPostDetails = useCallback(
    (options?: Omit<QueryOptions<QueryinstagramInteractionMentionedPostDetailsArgs, Query>, 'query'>) =>
      client.query<Query, QueryinstagramInteractionMentionedPostDetailsArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getInstagramInteractionMentionedPostDetails };
};
// Suspense Query Hooks
export const useInstagramInteractionMentionedPostDetailsSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QueryinstagramInteractionMentionedPostDetailsArgs>
) => {
  const result = useSuspenseQuery<Query, QueryinstagramInteractionMentionedPostDetailsArgs>(QUERY, {
    ...options
  });

  return result;
};
